import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const redirectTo = loc.state?.from || "/book";
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = await login(form.email.trim(), form.password);
      nav(user.role === "admin" ? "/admin" : redirectTo, { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-parchment">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-9 shadow-card">
          <div className="text-[10px] tracking-overline uppercase text-gold font-bold">{t("auth.welcome_back")}</div>
          <h1 className="font-display text-4xl text-navy-deep mt-1 mb-2">{t("auth.login_title")}</h1>
          <p className="text-sm text-slate-500 mb-7">{t("auth.login_subtitle")}</p>
          {err && (
            <div className="mb-5 px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="login-error">{err}</div>
          )}
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">{t("auth.email")}</label>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white"
                placeholder="you@example.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">{t("auth.password")}</label>
              <input
                type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit" disabled={busy}
              className="w-full bg-navy text-parchment py-3.5 rounded-sm hover:bg-gold transition flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit"
            >
              {busy && <Loader2 size={16} className="animate-spin"/>}
              {busy ? t("auth.signing_in") : t("auth.signin_btn")}
            </button>
          </form>
          <p className="mt-7 text-sm text-slate-500 text-center">
            {t("auth.no_account")} <Link to="/register" className="text-gold hover:underline" data-testid="login-register-link">{t("auth.signup_link")}</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
